import PropTypes from "prop-types";

import styled, { css, keyframes } from "styled-components";

const sizes = {
    "short": "100px",
    medium: "150px",
    "long": "200px"
};

const pulse = keyframes`
    0% {
        background-color: rgba(165, 165, 165, 0.1);
    }
    50% {
        background-color: rgba(165, 165, 165, 0.3);
    }
    100% {
        background-color: rgba(165, 165, 165, 0.1);
    }
`;

const pulseWhite = keyframes`
    0% {
        background-color: rgba(255, 255, 255, 0.1);
    }
    
    50% {
        background-color: rgba(255, 255, 255, 0.3);
    }
    
    100% {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

export const Placeholder = styled.div`
    height: 15px;
    margin: 10px 0;
    animation: ${pulse} 1s infinite ease-in-out;
    width: ${({ size }) => (typeof size === "string" ? sizes[size] : `${size}px`)};
    
    ${({ noMargin }) => noMargin && css`
        margin: 0;
    `}
    
    ${({ white }) => white && css`
        animation: ${pulseWhite} 1s infinite ease-in-out;
    `}
    
    ${({ inline }) => inline && css`
        display: inline-block;
        vertical-align: bottom;
    `}
`;

Placeholder.defaultProps = {
    size: "short",
    white: false,
    inline: false,
    noMargin: false
};

Placeholder.propTypes = {
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["short", "medium", "long"])]),
    white: PropTypes.bool,
    inline: PropTypes.bool,
    noMargin: PropTypes.bool
};

export function MultilinePlaceholder({ style = {} }) {
    return (
        <svg
            aria-hidden="true"
            width="340"
            height="90"
            viewBox="0 0 340 90"
            xmlns="http://www.w3.org/2000/svg"
            className="d-block"
            style={style}
        >
            <clipPath id="diff-placeholder">
                <rect x="0" y="0" width="67" height="15" rx="2" />
                <rect x="18" y="48" width="1001" height="15" rx="2" />
                <rect x="0" y="72" width="38" height="15" rx="2" />
                <rect x="127" y="48" width="54" height="15" rx="2" />
                <rect x="187" y="48" width="73" height="15" rx="2" />
                <rect x="76" y="0" width="140" height="15" rx="2" />
                <rect x="17" y="24" width="140" height="15" rx="2" />
                <rect x="166" y="24" width="174" height="15" rx="2" />
            </clipPath>
            
            <path
                className="js-diff-placeholder"
                clipPath="url(#diff-placeholder)"
                d="M0 0h340v90H0z"
                fill="#eee"
                fillRule="evenodd"
            />
        </svg>
    );
}

export function BarChartPlaceholder({ style = {} }) {
    return (
        <SVGContainer className="bar-chart-placeholder">
            <svg
                viewBox="0 0 1000 500"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="xMinYMin meet"
                style={style}
            >
                <path d="  M0,500  h88.8888888888889  v-259.3996933524655  q0,-5 -5,-5  h-78.8888888888889  q-5,0 -5,5  Z" fill="#c1c1c1" />
                <path d="  M111.11111111111111,500  h88.8888888888889  v-349.5707080315339  q0,-5 -5,-5  h-78.8888888888889  q-5,0 -5,5  Z" fill="#c1c1c1" />
                <path d="  M222.22222222222223,500  h88.8888888888889  v-253.05504832260493  q0,-5 -5,-5  h-78.8888888888889  q-5,0 -5,5  Z" fill="#c1c1c1" />
                <path d="  M333.33333333333337,500  h88.8888888888889  v-221.97943151405525  q0,-5 -5,-5  h-78.8888888888889  q-5,0 -5,5  Z" fill="#c1c1c1" />
                <path d="  M444.44444444444446,500  h88.8888888888889  v-358.5073177060536  q0,-5 -5,-5  h-78.8888888888889  q-5,0 -5,5  Z" fill="#c1c1c1" />
                <path d="  M555.5555555555555,500  h88.8888888888889  v-209.8313085875949  q0,-5 -5,-5  h-78.8888888888889  q-5,0 -5,5  Z" fill="#c1c1c1" />
                <path d="  M666.6666666666667,500  h88.8888888888889  v-168.46148828137854  q0,-5 -5,-5  h-78.8888888888889  q-5,0 -5,5  Z" fill="#c1c1c1" />
                <path d="  M777.7777777777778,500  h88.8888888888889  v-340.2075828748726  q0,-5 -5,-5  h-78.8888888888889  q-5,0 -5,5  Z" fill="#c1c1c1" />
                <path d="  M888.8888888888889,500  h88.8888888888889  v-265.829384552664  q0,-5 -5,-5  h-78.8888888888889  q-5,0 -5,5  Z" fill="#c1c1c1" />
            </svg>
        </SVGContainer>
    );
}

export function TrendChartPlaceholder({ style = {} }) {
    return (
        <SVGContainer className="bar-chart-placeholder">
            <svg
                viewBox="0 0 1000 500"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="xMinYMin meet"
                style={style}
            >
                <path
                    d="M 0,326.6872359370294 C 3.7500000000000004,323.73046633033294 117.5,230.42508028222443 125,228.12824904714745 C 132.5,225.83141781207047 242.5,254.401514312295 250,250.12619476779724 C 257.5,245.85087522329948 367.5,83.96105554585723 375,85.61759756388909 C 382.5,87.27413958192095 492.5,298.37651075295673 500,305.34426203552584 C 507.5,312.31201331809496
                 617.5,317.39150968533374 625,317.8759736495257 C 632.5,318.36043761371764 742.5,319.8365448863757 750,321.49306084192483 C 757.5,323.14957679747397 867.5,371.78813347365025 875,373.0931721678311 C 882.5,374.3982108620119 996.25,365.2373152935489 1000,364.9943506479525,L 1000 500,L 0 500Z"
                    fill="#c4c4c41a"
                />
                <path
                    d="M 0,326.6872359370294 C 3.7500000000000004,323.73046633033294 117.5,230.42508028222443 125,228.12824904714745 C 132.5,225.83141781207047 242.5,254.401514312295 250,250.12619476779724 C 257.5,245.85087522329948 367.5,83.96105554585723 375,85.61759756388909 C 382.5,87.27413958192095 492.5,298.37651075295673 500,305.34426203552584 C 507.5,312.31201331809496
                 617.5,317.39150968533374 625,317.8759736495257 C 632.5,318.36043761371764 742.5,319.8365448863757 750,321.49306084192483 C 757.5,323.14957679747397 867.5,371.78813347365025 875,373.0931721678311 C 882.5,374.3982108620119 996.25,365.2373152935489 1000,364.9943506479525"
                    fill="none"
                    stroke="#c4c4c4"
                    strokeWidth="9px"
                />
            </svg>
        </SVGContainer>
    );
}

const SVGContainer = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    
    opacity: 0.5;

    
`;
